import { driver, Config, DriveStep, Driver } from "driver.js";
import "driver.js/dist/driver.css";
import { translate } from "../../Common/translations";
import { useAnalytics } from "../../Common/Analytics/useAnalytics";

export type OnboardingTourOptions = {
    hasCategories: boolean;
    onComplete: () => void;
};

const MOBILE_DRAWER_TRANSITION_MS = 300;

function isMobileSidebar(): boolean {
    return !window.matchMedia("(min-width: 1024px)").matches;
}

export function useOnboardingTour(options: OnboardingTourOptions) {
    const { trackEvent, events } = useAnalytics();

    let openedDrawerForTour = false;

    function openMobileDrawerIfNeeded(driverObj: Driver): void {
        const sidebar = document.getElementById("admin-sidebar");
        const hamburger = document.getElementById("admin-sidebar-hamburger");
        if (!isMobileSidebar() || !sidebar || !hamburger || sidebar.classList.contains("is-open")) {
            return;
        }

        hamburger.click();
        openedDrawerForTour = true;
        setTimeout(() => driverObj.refresh(), MOBILE_DRAWER_TRANSITION_MS);
    }

    function closeMobileDrawerIfOpenedByTour(): void {
        if (!openedDrawerForTour) {
            return;
        }

        document.getElementById("admin-sidebar-hamburger")?.click();
        openedDrawerForTour = false;
    }

    const steps: DriveStep[] = [
        {
            element: "#admin-sidebar",
            popover: {
                title: translate("post_wizard_onboarding.tour.step_nav.title"),
                description: translate("post_wizard_onboarding.tour.step_nav.description"),
            },
            onHighlightStarted: (_element, _step, { driver: driverObj }) => openMobileDrawerIfNeeded(driverObj),
            onDeselected: () => closeMobileDrawerIfOpenedByTour(),
        },
        {
            element: "#global-setup-checklist-app",
            popover: {
                title: translate("post_wizard_onboarding.tour.step_checklist.title"),
                description: translate("post_wizard_onboarding.tour.step_checklist.description"),
            },
        },
        {
            element: "#post-wizard-tour-categories-link",
            popover: {
                title: translate("post_wizard_onboarding.tour.step_categories.title"),
                description: options.hasCategories
                    ? translate("post_wizard_onboarding.tour.step_categories.description_has_categories")
                    : translate("post_wizard_onboarding.tour.step_categories.description_no_categories"),
            },
            onHighlightStarted: (_element, _step, { driver: driverObj }) => openMobileDrawerIfNeeded(driverObj),
            onDeselected: () => closeMobileDrawerIfOpenedByTour(),
        },
        {
            element: "#post-wizard-tour-event-status",
            popover: {
                title: translate("post_wizard_onboarding.tour.step_status.title"),
                description: translate("post_wizard_onboarding.tour.step_status.description"),
            },
        },
    ];

    function start() {
        const driverObj = driver({
            showProgress: true,
            allowClose: false,
            steps,
            nextBtnText: translate("common.next"),
            prevBtnText: translate("common.back"),
            doneBtnText: translate("checklist.done_label"),
            onDestroyed: (_element, _step, { state }) => {
                const completed = state.activeIndex === steps.length - 1;
                if (completed) {
                    trackEvent(events.Organizer.OnboardingTourCompleted);
                    options.onComplete();
                } else {
                    trackEvent(events.Organizer.OnboardingTourAbandoned);
                }
            },
        } satisfies Config);

        driverObj.drive();
    }

    return { start };
}
